import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ToolTipWrapper } from "@/Shared/Components/QuestionBubble";
import { InvoiceWarningState } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceWarningState";
import type { ReactNode } from "react";

interface WarningProps {
  label: string;
  variant: "warning" | "danger";
  explanation: ReactNode;
}

function Warning_(props: WarningProps) {
  return (
    <>
      <ToolTipWrapper
        // Every warning here carries the one link that resolves it, so the card
        // has to accept the pointer.
        interactive
        description={
          <div className="flex flex-col items-start">{props.explanation}</div>
        }
      >
        <Badge label={props.label} variant={props.variant} />
      </ToolTipWrapper>
      <div className="w-[8px] shrink-0" />
    </>
  );
}

function Explanation_({
  children,
  action,
}: {
  children: ReactNode;
  action?: { label: string; href: string } | undefined;
}) {
  return (
    <>
      <div>{children}</div>
      {action !== undefined && (
        <>
          <div className="h-[8px]" />
          <div>
            <Button size="link" href={action.href}>
              {action.label}
            </Button>
          </div>
        </>
      )}
    </>
  );
}

interface InvoicesNavBarInvoiceWarningBadgeProps {
  invoiceWarningState: InvoiceWarningState;
}

/**
 * The badge itself, given a warning state. Split out from
 * NavBarInvoiceWarning so all six states can be seen without standing
 * up an account in each condition.
 *
 * Warning-toned states are advance notice that booking will stop; danger-toned
 * ones mean it already has. Each carries the one link that resolves it, except
 * the two that need us to act, which point at support instead.
 */
export function NavBarInvoiceWarningBadge(
  props: InvoicesNavBarInvoiceWarningBadgeProps,
) {
  switch (props.invoiceWarningState) {
    case InvoiceWarningState.BookingSuspendedDueToOverCreditLimit:
      return (
        <Warning_
          label="Booking Paused: Credit Limit Exceeded"
          variant="danger"
          explanation={
            <Explanation_
              action={{ label: "Make a payment", href: "/view-invoices" }}
            >
              You have exceeded your credit limit. Please pay invoices to
              restore booking functionality
            </Explanation_>
          }
        />
      );
    case InvoiceWarningState.BookingSuspendedDueToOverdueInvoices:
      return (
        <Warning_
          label="Booking Paused: Overdue Invoices"
          variant="danger"
          explanation={
            <Explanation_
              action={{ label: "View Invoices", href: "/view-invoices" }}
            >
              You have overdue invoices. Please pay immediately to restore
              booking functionality.
            </Explanation_>
          }
        />
      );
    case InvoiceWarningState.BookingWarningCloseToCreditLimit:
      return (
        <Warning_
          label="Close to credit limit"
          variant="warning"
          explanation={
            <Explanation_
              action={{ label: "View Invoices", href: "/view-invoices" }}
            >
              You are close to your credit limit. Booking will be disabled when
              limit is exceeded.
            </Explanation_>
          }
        />
      );
    case InvoiceWarningState.BookingWarningInvoicesCloseToOverdue:
      return (
        <Warning_
          label="Invoices close to due date"
          variant="warning"
          explanation={
            <Explanation_
              action={{ label: "View Invoices", href: "/view-invoices" }}
            >
              There are invoices close to being overdue. Booking will be
              disabled when invoices become overdue.
            </Explanation_>
          }
        />
      );
    case InvoiceWarningState.BookingSuspendedNeedsAccountVerification:
      return (
        <Warning_
          label="Account Verification Required"
          variant="warning"
          explanation={
            <Explanation_>
              Our team must verify your shipment before you are able to book.
              Please contact us for assistance. You can use the live chat
              functionality in the bottom right of the screen, or send an email
              to support@freightsimple.com
            </Explanation_>
          }
        />
      );
    case InvoiceWarningState.BookingSuspendedAccountOnHold:
      return (
        <Warning_
          label="Booking Paused: Account On Hold"
          variant="danger"
          explanation={
            <Explanation_>
              Booking is paused on your account. Please contact us to resolve
              it. You can use the live chat in the bottom right of the screen,
              or send an email to support@freightsimple.com
            </Explanation_>
          }
        />
      );
    // Spelled out rather than left to `default`, so adding a warning state to
    // the API makes this switch fail the exhaustiveness check instead of
    // silently rendering nothing.
    case InvoiceWarningState.NoWarning:
      return <></>;
  }
}
